/**
 * The account's database tokens, and what each of them may do.
 *
 * A token authorises row reads and writes over the API without a session, so
 * the permission grid is the point of the section rather than an aside: a new
 * token arrives with the run of the whole workspace, and this is the only place
 * that narrows it.
 *
 * Each toggle saves on its own. The grid has no draft state to submit -- there
 * are four operations across every table in the workspace, and a Save button
 * over that many checkboxes only creates a window in which the screen and the
 * key disagree about what the key can do.
 */
import { useState } from 'react'
import { ChevronDown, ChevronRight, Eye, EyeOff, MoreHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { DatabaseApplication, DatabaseToken } from '@/api/types'
import { useApplications } from '@/features/application/queries'
import { useTenantWorkspace } from '@/features/workspace/use-tenant-workspace'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { CopyButton } from '@/shared/components/copy-button'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Separator } from '@/shared/ui/separator'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  useCreateDatabaseToken,
  useDatabaseTokens,
  useDeleteDatabaseToken,
  useUpdateDatabaseToken,
} from '../queries'
import {
  TOKEN_OPERATIONS,
  databaseCoverage,
  databasesOf,
  prune,
  setDatabase,
  setTable,
  setWorkspace,
  tableCovered,
  workspaceCoverage,
  type Coverage,
  type TokenOperation,
} from '../tokens'

export function DatabaseTokens() {
  const { t } = useTranslation()
  const { workspace } = useTenantWorkspace()
  const { data: tokens } = useDatabaseTokens()

  if (workspace === undefined || tokens === undefined)
    return <Skeleton className="h-24" />

  // Only this workspace's tokens. The endpoint answers for the whole account and
  // the tenant is the only workspace there is, so anything else would be a token
  // whose databases this page cannot name, shown with an empty grid.
  const mine = tokens.filter((token) => token.workspace === workspace.id)

  return (
    <div className="space-y-4">
      {mine.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('databaseToken.empty')}</p>
      ) : (
        <ul aria-label={t('databaseToken.title')} className="space-y-3">
          {mine.map((token) => (
            <li key={token.id}>
              <TokenCard token={token} workspaceId={workspace.id} />
            </li>
          ))}
        </ul>
      )}

      <Separator />

      <CreateToken workspaceId={workspace.id} />
    </div>
  )
}

function CreateToken({ workspaceId }: { workspaceId: number }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const create = useCreateDatabaseToken()

  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate(
          { name: name.trim(), workspaceId },
          {
            onSuccess: () => {
              setName('')
            },
          },
        )
      }}
    >
      <div className="min-w-0 flex-1 space-y-1.5">
        <Label htmlFor="token-name">{t('databaseToken.name')}</Label>
        <Input
          id="token-name"
          value={name}
          placeholder={t('databaseToken.namePlaceholder')}
          onChange={(event) => {
            setName(event.target.value)
          }}
        />
      </div>
      <Button type="submit" disabled={name.trim() === '' || create.isPending}>
        {t('databaseToken.create')}
      </Button>
    </form>
  )
}

function TokenCard({
  token,
  workspaceId,
}: {
  token: DatabaseToken
  workspaceId: number
}) {
  const { t } = useTranslation()
  const { data: applications } = useApplications(workspaceId)
  const databases = databasesOf(applications)

  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [name, setName] = useState(token.name)

  const update = useUpdateDatabaseToken()
  const remove = useDeleteDatabaseToken()

  // Entries naming a database deleted in another tab are dropped before the
  // write rather than after it: the endpoint rejects the whole update over one
  // of them, which would leave this token unsavable until a reload.
  const save = (permissions: DatabaseToken['permissions']): void => {
    update.mutate({
      tokenId: token.id,
      values: { permissions: prune(permissions, databases) },
    })
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={t('databaseToken.name')}
          className="h-8 w-48"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
          onBlur={() => {
            const trimmed = name.trim()
            if (trimmed === '' || trimmed === token.name) {
              setName(token.name)
              return
            }
            update.mutate({ tokenId: token.id, values: { name: trimmed } })
          }}
        />

        <code className="min-w-0 flex-1 truncate rounded-md border bg-muted px-3 py-1.5 font-mono text-sm">
          {revealed ? token.key : '•'.repeat(32)}
        </code>
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          aria-label={revealed ? t('databaseToken.hide') : t('databaseToken.show')}
          onClick={() => {
            setRevealed((shown) => !shown)
          }}
        >
          {revealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </Button>
        <CopyButton value={token.key} label={t('databaseToken.copy')} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={t('databaseToken.more', { name: token.name })}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => {
                update.mutate(
                  { tokenId: token.id, values: { rotate_key: true } },
                  {
                    // Revealed on rotation: the old key stops working the moment
                    // this returns, so whatever is using it has to be given the
                    // new one now.
                    onSuccess: () => {
                      setRevealed(true)
                    },
                  },
                )
              }}
            >
              {t('databaseToken.rotate')}
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => {
                setConfirming(true)
              }}
            >
              {t('action.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-end justify-between gap-4">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-1 font-normal"
          aria-expanded={open}
          onClick={() => {
            setOpen((shown) => !shown)
          }}
        >
          {open ? <ChevronDown /> : <ChevronRight />}
          {t('databaseToken.showDatabases')}
        </Button>
        <div className="flex gap-4 text-xs text-muted-foreground">
          {TOKEN_OPERATIONS.map((operation) => (
            <span key={operation} className="w-10 text-center">
              {t(
                `databaseToken.operations.${operation}` as 'databaseToken.operations.read',
              )}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium">{t('databaseToken.allTables')}</span>
        <OperationRow
          label={t('databaseToken.allTables')}
          coverage={(operation) =>
            workspaceCoverage(token.permissions[operation] ?? false)
          }
          onChange={(operation, on) => {
            save(setWorkspace(token.permissions, operation, on))
          }}
        />
      </div>

      {open &&
        (databases.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('databaseToken.noDatabases')}
          </p>
        ) : (
          <ul className="space-y-1">
            {databases.map((database) => (
              <Collapsible key={database.id} asChild>
                <li className="group/database space-y-1">
                  <div className="flex items-center justify-between gap-4">
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 min-w-0 justify-start gap-1 px-1"
                      >
                        <ChevronRight className="transition-transform group-data-[state=open]/database:rotate-90" />
                        <span className="truncate">{database.name}</span>
                      </Button>
                    </CollapsibleTrigger>
                    <OperationRow
                      label={database.name}
                      coverage={(operation) =>
                        databaseCoverage(token.permissions[operation] ?? false, database)
                      }
                      onChange={(operation, on) => {
                        save(
                          setDatabase(
                            token.permissions,
                            operation,
                            databases,
                            database,
                            on,
                          ),
                        )
                      }}
                    />
                  </div>
                  <CollapsibleContent>
                    <TableRows
                      token={token}
                      databases={databases}
                      database={database}
                      onChange={save}
                    />
                  </CollapsibleContent>
                </li>
              </Collapsible>
            ))}
          </ul>
        ))}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('databaseToken.deleteTitle')}
        description={t('databaseToken.deleteDescription', { name: token.name })}
        confirmLabel={t('action.delete')}
        destructive
        onConfirm={() => {
          remove.mutate(token.id)
        }}
      />
    </div>
  )
}

function TableRows({
  token,
  databases,
  database,
  onChange,
}: {
  token: DatabaseToken
  databases: DatabaseApplication[]
  database: DatabaseApplication
  onChange: (permissions: DatabaseToken['permissions']) => void
}) {
  return (
    <ul className="space-y-1">
      {database.tables.map((table) => (
        <li key={table.id} className="flex items-center justify-between gap-4 pl-10">
          <span className="truncate text-sm text-muted-foreground">{table.name}</span>
          <OperationRow
            label={table.name}
            coverage={(operation) =>
              tableCovered(token.permissions[operation] ?? false, database, table.id)
                ? 'all'
                : 'none'
            }
            onChange={(operation, on) => {
              onChange(
                setTable(token.permissions, operation, databases, database, table.id, on),
              )
            }}
          />
        </li>
      ))}
    </ul>
  )
}

/**
 * The four checkboxes on one row.
 *
 * A partly covered scope is shown as `indeterminate` rather than as ticked: a
 * row that reads as "allowed" while two of its tables are not is the one
 * mistake a permission grid must not make.
 */
function OperationRow({
  label,
  coverage,
  onChange,
}: {
  label: string
  coverage: (operation: TokenOperation) => Coverage
  onChange: (operation: TokenOperation, on: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex gap-4">
      {TOKEN_OPERATIONS.map((operation) => {
        const state: Coverage = coverage(operation)
        return (
          <div key={operation} className="flex w-10 justify-center">
            <Checkbox
              className="data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary/30"
              aria-label={`${t(
                `databaseToken.operations.${operation}` as 'databaseToken.operations.read',
              )} — ${label}`}
              checked={
                state === 'all' ? true : state === 'some' ? 'indeterminate' : false
              }
              onCheckedChange={(on) => {
                onChange(operation, on === true)
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
